import type { EmitToRoomOptions } from "@orsane/schema/realtime";
import type React from "react";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import type { Socket } from "socket.io-client";
import SuperJSON from "superjson";
import type z from "zod";
import { useSessionQuery } from "@/queries/queries";
import socketService from "./SocketService";

const SocketContext = createContext<Socket | null>(null);

export const SocketProvider = ({ children }: { children: React.ReactNode }) => {
    const [socket, setSocket] = useState<Socket | null>(null);

    useEffect(() => {
        const sock = socketService.connect({
            onConnected: () => {},
            onDisconnected: () => {},
        });
        setSocket(sock);

        return () => {
            socketService.disconnect();
        };
    }, []);

    return <SocketContext.Provider value={socket}>{children}</SocketContext.Provider>;
};

export const useSocket = () => {
    return useContext(SocketContext);
};

export const useSocketMessage = <TSchema extends z.ZodSchema>({
    event,
    schema,
    onConnected,
    callback,
}: {
    event: string;
    schema: TSchema;
    onConnected?: (socket: Socket) => void;
    callback: (data: z.infer<TSchema>) => void;
}) => {
    const socket = useSocket();
    const callbackRef = useRef(callback);
    const schemaRef = useRef(schema);
    const { data: sessionData } = useSessionQuery();

    // Keep refs up to date
    useEffect(() => {
        callbackRef.current = callback;
    }, [callback]);

    useEffect(() => {
        schemaRef.current = schema;
    }, [schema]);

    useEffect(() => {
        if (!socket) return;

        const handler = (rawMessage: string) => {
            const currentUserId = sessionData?.user?.id;
            const jsonMessage: { data: z.infer<typeof schemaRef.current>; options: EmitToRoomOptions } = SuperJSON.parse(rawMessage);

            if (currentUserId && jsonMessage.options.excludeUserIds?.includes(currentUserId)) {
                return;
            }

            console.log("Received socketio message: ", currentUserId, jsonMessage.options.excludeUserIds);
            const result = schemaRef.current.safeParse(jsonMessage.data);
            if (!result.success) {
                console.error("Invalid data received from socket:", result.error);
                return;
            }
            callbackRef.current(result.data);
        };

        if (onConnected) {
            onConnected(socket);
        }

        socket.on(event, handler);

        return () => {
            socket.off(event);
        };
    }, [socket, event]);

    return {
        isConnected: socket?.connected,
    };
};
